import { AimsCommonConfirmDialogComponent, AimsCommonPopupEditorComponent, AimsCommonTableHeader, AimsCommonTableHeaderType, AimsCommonTablePermissions } from '@aims/common-components';
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnInit, Output, PLATFORM_ID, ViewChild, Inject } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, BehaviorSubject, Subject, Observable, of, forkJoin, tap } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { DialogComponent } from 'src/app/services/message/message.enum';
import { MessageService } from 'src/app/services/message/message.service';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import { CacheItem, TransferHttpService } from 'src/app/services/transfer-http/transfer-http.service';
import { AllLocations, AllSites, AllSubLocations, InvConfig, InvFileAttachment, InvInventoryGroup, InvInventoryItemReceived, LogShippingCommLi, LogUnitOfIssue } from 'src/models';
import { InvInventoryItem } from 'src/models/InvInventoryItem';
import { InventoryMovePopupComponent } from './inventory-move-popup/inventory-move-popup.component';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-inventory-item',
  templateUrl: './inventory-item.component.html',
  styleUrls: ['./inventory-item.component.css']
})
export class InventoryItemComponent implements OnInit, AfterViewInit {
  //Spinner
  spinnerText: string = "Loading...";
  showSpinner: boolean = false;

  //Form Control
  newItem: boolean;
  shouldClearWholeForm: boolean;
  today = new Date();
  inventoryItemReceived: InvInventoryItemReceived = new InvInventoryItemReceived() ;
  inventoryItem: InvInventoryItem = new InvInventoryItem();
  originalInventoryItem: InvInventoryItem;
  expendedInventoryItemQuantity: number = 0;
  expendedInventoryItemNote: string = null;
  documents: InvFileAttachment[] = [];
  title: String;

  @Output() LineItemInventoriedEvent = new EventEmitter();
  @Output() InventoryItemClearedEvent = new EventEmitter();

  private _lineItem: LogShippingCommLi;
  @Input()
    get lineItem(): LogShippingCommLi { return this._lineItem; };
    set lineItem(lineItem: LogShippingCommLi) {
      this._lineItem = lineItem;
      this.inventoryItem = new InvInventoryItem();

      if (this._lineItem && this._lineItem.shippingLineItemId != 0) {
        this.inventoryItem.description = this.lineItem.po?.poLnDesc ||
                                          this.lineItem.reqLineItem?.nomenclature ||
                                          this.lineItem.shippingRequestItem?.nomenclature;
        this.inventoryItem.unitOfIssueId = this.lineItem.po?.poLnUmCd || this.lineItem.reqLineItem?.ui || this.lineItem.shippingRequestItem?.ui

        this.inventoryItemReceived.quantity = this.inventoryItemReceived.originalQuantity = this.lineItem.inventoryQuantity ?? 0;
        this.inventoryItemReceived.costPerUnit = this.lineItem.po?.netUnitCstAmt || this.lineItem.reqLineItem?.unitPrice || 0;

        this.inventoryItemReceived.inventoryItemId = this.inventoryItem.inventoryItemId;
      }
      else {
        this.inventoryItemReceived.quantity = this.inventoryItemReceived.originalQuantity = 0;
        this.inventoryItemReceived.costPerUnit = 0;
      }
    }

  // does client use barcodes for item id
  public barcodeItemNo: boolean = true;

  destroy$: Subject<boolean> = new Subject();

  tenantConfig = this.transferHttp.tenantConfig;

  invConfig = this.transferHttp.getSingle<InvConfig>('inventory/GetInventoryConfig');
  activeUnitValues = this.transferHttp.getSingle('logistics/GetActiveUnitOfIssue');
  inventoryGroups: Observable<InvInventoryGroup> = this.transferHttp.getSingle('Inventory/GetInventoryGroups')
  activeInventoryGroups: Observable<InvInventoryGroup> = this.transferHttp.getSingle('Inventory/GetActiveInventoryGroups')
  owners = this.transferHttp.getSingle('Facilities/GetOwners');
  activeOwners = this.transferHttp.getSingle('Facilities/GetActiveOwners');
  manufacturers = this.transferHttp.getSingle('Facilities/GetManufacturers');
  errcCodes = this.transferHttp.getSingle('Logistics/GetErrcCodes');
  workSections = this.transferHttp.getSingle('Hr/GetWorkSections');

  // Site/Location/SubLocation
  loc = this.transferHttp.getSingle<Array<AllLocations>>('Facilities/GetLocations');
  sl = this.transferHttp.getSingle<Array<AllSubLocations>>('Facilities/GetSubLocations');
  sites: Observable<AllSites[]> = this.transferHttp.getSingle<Array<AllSites>>('Common/GetAllSites');

  siteSubj: BehaviorSubject<number> = new BehaviorSubject(this.inventoryItem.siteId);
  locationSubj: BehaviorSubject<number> = new BehaviorSubject(this.inventoryItem.locationId);

  locations = combineLatest([this.loc, this.siteSubj]).pipe(
    map(([locArr, siteId]) => locArr.filter(l => l.siteId == siteId)),
    tap((locArr) => {
      //null out locationId if not included in current values
      if (!locArr.map(l => l.locationId).includes(this.inventoryItem.locationId)) {
        this.inventoryItem.locationId = null;
        this.locationSubj.next(null);
      }
    })
  );
  sublocations = combineLatest([this.locationSubj, this.sl]).pipe(
    map(([locId, slArr]) => slArr.filter(sl => sl.locationId == locId)),
    tap((slArr) => {
      if (!slArr.map(s => s.subLocationId).includes(this.inventoryItem.subLocationId)) {
        this.inventoryItem.subLocationId = null;
      }
    })
  );

  public inventoryConfig: InvConfig;

  @ViewChild('invForm') invForm: NgForm;
  @ViewChild('barcode') barcode: ElementRef;

  constructor(private transferHttp: TransferHttpService,
    private route: ActivatedRoute,
    private router: Router,
    public permissions: PermissionsService,
    private messageService: MessageService,
    private dialog: MatDialog,
    @Inject(PLATFORM_ID) private platformId: Object) {
  }

  sourcesRetrieved: boolean = false;
  inventoryItemAndDocsLoaded: boolean = false;
  currentQuantity: number = 0;

  locationColumns = [
                { key: 'location', label: 'Location', filter: true, type: AimsCommonTableHeaderType.STRING }
            ];
  sublocationColumns = [
                { key: 'subLocation', label: 'Sub-Location', filter: true, type: AimsCommonTableHeaderType.STRING }
            ];
  manfColumns = [
                { key: 'manufacturer', label: 'Manufacturer', filter: true, type: AimsCommonTableHeaderType.STRING }
            ];

  ngOnInit(): void {
    window.scrollTo(0,0);
    this.sourcesRetrieved = false;
    this.inventoryItemAndDocsLoaded = false;
    this.showSpinner = true;

    const allSources = forkJoin({
      sourceActiveUnitValues: this.activeUnitValues,
      sourceInventoryGroups: this.inventoryGroups,
      sourceOwners: this.owners,
      sourceManf: this.manufacturers,
      sourceErrc: this.errcCodes,
      sourceLoc: this.loc,
      sourceSl: this.sl,
      sourceInventoryConfig: this.invConfig
    });

    allSources.subscribe(val => {
      this.inventoryConfig = val.sourceInventoryConfig;
      this.sourcesRetrieved = true;
      this.checkSpinner();
    });

    // this.activeUnitValues.pipe(take(1)).subscribe();
    this.inventoryItemReceived.quantity = this.inventoryItemReceived.originalQuantity = 0;
    this.inventoryItemReceived.costPerUnit = 0.0;
    this.inventoryItemReceived.receivedDate = new Date();

    //If id provided, load existing req data
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(p => {
      if (p['id']) {
        this.inventoryItem.inventoryItemId = p['id'];
        this.loadInventoryItemById(this.inventoryItem.inventoryItemId);
      }
      else {
        this.newItem = (this.route && this.route?.toString()?.indexOf('Create') !== -1);
        this.inventoryItemAndDocsLoaded = true;
        this.checkSpinner();
        if (this.newItem) {
          this.inventoryItem.unitOfIssueId = 'EA ';
        }

        this.initializeForm()
      }
    });
  }

  ngOnDestroy() {
      this.destroy$.next(true);
      this.destroy$.unsubscribe();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.barcode?.nativeElement?.focus();
    },0);
  }

  initializeForm() : void {
    this.title = this.newItem ? "Create Inventory Item" : "Inventory Item";
  }

  public finishWithInventoryItem() {
    if (this.lineItem) {
      this.markLineItemAsInventoried();
    }
    else {
      this.clearForm();
    }
  }

  public clearReceivedForm() {
    this.inventoryItemReceived.quantity = this.inventoryItemReceived.originalQuantity = 0;
    this.inventoryItemReceived.costPerUnit = 0;
  }
  public clearExpendedForm() {
    this.expendedInventoryItemNote = null;
    this.expendedInventoryItemQuantity = 0;
  }

  public clearWholeForm() {
    this.shouldClearWholeForm = true;
    this.clearForm();
  }

  public clearForm() {
    this.clearReceivedForm();
    this.clearExpendedForm();

    if (this.shouldClearWholeForm) {
      this.InventoryItemClearedEvent.emit();
      this.inventoryItemReceived.receivedDate = new Date();
      this.lineItem = null;
      this.inventoryItem = new InvInventoryItem();
      this.originalInventoryItem = null;
      this.initializeForm();
      this.shouldClearWholeForm = false;
      this.invForm?.form?.markAsPristine();
    }
  }

  public saveInventoryItemAndClear(inventoryItem : InvInventoryItem) {
      this.shouldClearWholeForm = true;
      this.saveInventoryItem(inventoryItem);
  }

  public saveInventoryItem(inventoryItem : InvInventoryItem) {
    this.spinnerText = "Saving...";

    if (!this.newItem) {
        this.inventoryItemReceived.inventoryItemId = this.inventoryItem.inventoryItemId;

      // compare to original
      if (this.inventoryItemChanged())
      {
        this.showSpinner = true;
        this.transferHttp.put('inventory/SaveInventoryItem', inventoryItem).subscribe(retVal => {
          this.inventoryItemReceived.inventoryItemId = retVal;
          this.saveInventoryItemReceived();
          this.saveManuallyExpended();
          this.finishWithInventoryItem();
          this.showSpinner = false;
          this.invForm?.form?.markAsPristine();
        });
      }

      this.saveInventoryItemReceived();
      this.saveManuallyExpended();
      this.finishWithInventoryItem();
    }
    else {
      this.spinnerText = "Saving...";
      this.showSpinner = true;

      this.transferHttp.post('inventory/CreateInventoryItem', inventoryItem).subscribe({
        next: retVal => {
          const clearAndNew = this.shouldClearWholeForm;
          this.inventoryItemReceived.inventoryItemId = retVal;
          this.saveInventoryItemReceived();
          this.saveManuallyExpended();
          this.finishWithInventoryItem();
          this.showSpinner = false;
          this.invForm?.form?.markAsPristine();

          if (clearAndNew) {
            this.router.navigate(['/inventory/inventoryItemClear']);
          }
          else {
            this.router.navigate([`/inventory/inventoryItem/${retVal}`]);
          }
        },
        error: _ => {
          this.showSpinner = false;
        }
      });
    }
  }

  public saveInventoryItemReceived() : void {
    if (this.inventoryItemReceived.quantity == 0) {
      return;
    }

    this.inventoryItemReceived.originalQuantity = this.inventoryItemReceived.quantity;

    this.showSpinner = true;
    this.spinnerText = "Saving Inventory Item Received...";
      this.transferHttp.post('inventory/CreateInventoryItemReceived', this.inventoryItemReceived).subscribe(retVal => {
        this.showSpinner = false;
        this.invForm?.form?.markAsPristine();
      });
  }

  public saveManuallyExpended(): void {
    if (this.expendedInventoryItemQuantity == 0) {
      return;
    }

    this.showSpinner = true;
    this.spinnerText = "Saving Manually Expended...";
    var url = `Inventory/AddPartToManuallyExpended?inventoryItemId=${this.inventoryItem.inventoryItemId}&quantity=${this.expendedInventoryItemQuantity}&note=${this.expendedInventoryItemNote}`;
      this.transferHttp.put(url, null).subscribe(retVal => {
        this.showSpinner = false;
        this.invForm?.form?.markAsPristine();
        this.expendedInventoryItemQuantity = 0;
      });
  }

  public onValueChange(event) {
    var inventoryItemValue = event.target.value;
    if (inventoryItemValue.length === this.inventoryConfig.inventoryItemNumberLength) {
        this.loadInventoryItemByBarcode();
    }
  }

  loadInventoryItemByBarcode(): void {
      this.spinnerText = "Loading Inventory Item...";
      this.showSpinner = true;
      this.documents.length = 0;

      const barcode = this.inventoryItem.barcode;
      this.shouldClearWholeForm = true;
      if (this.lineItem == null) {
        this.clearForm();
      }
      this.inventoryItem.barcode = barcode;

      this.transferHttp.get(`inventory/GetInventoryItem?barcode=${this.inventoryItem.barcode}` ).subscribe({
        next: inventoryItem => {
          if (inventoryItem) {
            this.inventoryItem = inventoryItem;
            this.copyInventoryItem(inventoryItem);
            this.newItem = false;
            this.siteSubj.next(this.inventoryItem.siteId);
            this.locationSubj.next(this.inventoryItem.locationId);
            this.loadInventoryItemDocuments();
          }
          else {
            if (this.inventoryConfig.useAutoNumbering) {
              // show error
              this.showSpinner = false;
              alert("no item exists with that number");
            }
            else {
              this.newItem = true;
            }
          }
        },
        error: _ => {
          this.showSpinner = false;
        }
      });
  }

  loadInventoryItemById(inventoryItemId: number): void {
      this.spinnerText = "Loading Inventory Item...";
      this.showSpinner = true;
      this.documents.length = 0;

      this.shouldClearWholeForm = true;
      if (this.lineItem == null) {
        this.clearForm();
      }

      this.transferHttp.get(`inventory/GetInventoryItemById?id=${inventoryItemId}` ).subscribe({
        next: inventoryItem => {
        if (inventoryItem) {
          this.inventoryItem = inventoryItem;
          this.copyInventoryItem(inventoryItem);
          this.newItem = false;
          this.siteSubj.next(this.inventoryItem.siteId);
          this.locationSubj.next(this.inventoryItem.locationId);
          this.loadInventoryItemDocuments();
          this.currentQuantity = this.getInventoryQuantity(this.inventoryItem);
        }
        else {
          if (this.inventoryConfig.useAutoNumbering) {
            // show error
            alert("no item exists with that ID");
          }
          else {
            this.newItem = true;
          }
        }
      },
      error: _ => {
          this.showSpinner = false;
      }
    });
  }

  loadInventoryItemDocuments(): void {
    this.transferHttp.get(`inventory/GetInventoryItemDocuments?inventoryItemId=${this.inventoryItem.inventoryItemId}` ).subscribe(docs => {
      this.documents = docs;
      this.inventoryItemAndDocsLoaded = true;
        this.checkSpinner();
    });
  }

  private checkSpinner() {
    if (this.sourcesRetrieved && this.inventoryItemAndDocsLoaded) {
      this.showSpinner = false;
    }
  }

  public inventoryItemChanged() : boolean {
    return (this.inventoryItem.barcode !== this.originalInventoryItem.barcode ||
      this.inventoryItem.description !== this.originalInventoryItem.description ||
      this.inventoryItem.partNo !== this.originalInventoryItem.partNo ||
      this.inventoryItem.isActive !== this.originalInventoryItem.isActive || 
      this.inventoryItem.assemblyIssued !== this.originalInventoryItem.assemblyIssued ||
      this.inventoryItem.hazmat !== this.originalInventoryItem.hazmat ||
      this.inventoryItem.workOrderRequired !== this.originalInventoryItem.workOrderRequired ||
      this.inventoryItem.missionCritical !== this.originalInventoryItem.missionCritical ||
      this.inventoryItem.minQty !== this.originalInventoryItem.minQty ||
      this.inventoryItem.authorizedQty !== this.originalInventoryItem.authorizedQty ||
      this.inventoryItem.siteId !== this.originalInventoryItem.siteId ||
      this.inventoryItem.locationId !== this.originalInventoryItem.locationId ||
      this.inventoryItem.subLocationId !== this.originalInventoryItem.subLocationId ||
      this.inventoryItem.unitOfIssueId !== this.originalInventoryItem.unitOfIssueId ||
      this.inventoryItem.inventoryGroupId !== this.originalInventoryItem.inventoryGroupId ||
      this.inventoryItem.ownerId !== this.originalInventoryItem.ownerId ||
      this.inventoryItem.manufacturerId !== this.originalInventoryItem.manufacturerId ||
      this.inventoryItem.note != this.originalInventoryItem.note ||
      this.inventoryItem.difm != this.originalInventoryItem.difm ||
      this.inventoryItem.govtFurnished != this.originalInventoryItem.govtFurnished ||
      this.inventoryItem.returnToGovt != this.originalInventoryItem.returnToGovt ||
      this.inventoryItem.expirationDate != this.originalInventoryItem.expirationDate ||
      this.inventoryItem.nsn != this.originalInventoryItem.nsn ||
      this.inventoryItem.serialNo != this.originalInventoryItem.serialNo ||
      this.inventoryItem.errc != this.originalInventoryItem.errc ||
      this.inventoryItem.autoReorder != this.originalInventoryItem.autoReorder ||
      this.inventoryItem.workSectionId != this.originalInventoryItem.workSectionId);
  }

  private copyInventoryItem(inventoryItem: InvInventoryItem) {
    this.originalInventoryItem = {...inventoryItem};
  }

  public showAssemblyAlreadyReceived(): boolean {
  return (this.inventoryItem.inventoryItemId !== 0 &&
        (this.inventoryItem.assemblyIssued && this.inventoryItem.invInventoryItemReceived.length == 1));
  }

  private markLineItemAsInventoried() : void {
    this.spinnerText = "Marking Line Item as Inventoried...";
    this.showSpinner = true;
    var url = `inventory/ClearInventoryOnLineItem?lineItemId=${this.lineItem.shippingLineItemId}`;
    this.transferHttp.put(url, null)
      .subscribe(retVal => {
        this.LineItemInventoriedEvent.emit();
        this.showSpinner = false;
        this.lineItem = null;
        this.clearForm();
    });
  }

  siteChange(change: MatSelectChange) {
    this.siteSubj.next(change.value);
  }

  locationChange(change: number) {
    if (change) {
      this.locationSubj.next(change);
    }
  }

  onAssyIssuedChange(event) {
    if (this.inventoryItem.assemblyIssued) {
      this.inventoryItemReceived.quantity = this.inventoryItemReceived.originalQuantity = 1;
    }
  }

  public moveInventoryItem(inventoryItem: InvInventoryItem) {
    this.openPopup(inventoryItem);
  }

  public openPopup(itemToMove: InvInventoryItem) {
    const dialogRef = this.dialog.open(AimsCommonPopupEditorComponent, {
            data: {
                header: 'Move Item Quantity',
                content: InventoryMovePopupComponent,
                contentData: {
                  itemToMove: itemToMove,
                  sites: this.sites,
                  locations: this.locations,
                  sublocations: this.sublocations,
                  transferHttp: this.transferHttp
                },
            },
            disableClose: true
        });

        dialogRef.afterClosed().subscribe(dialogResult => {
          if (dialogResult) {
            let quantity: number
            let targetBarcode: string = dialogResult.quantity;

            if (dialogResult.target == 'new') {
              targetBarcode = dialogResult.newbarcode;
            }
            else if (dialogResult.target == 'existing') {
              targetBarcode = dialogResult.itemLookup;
            }
            else { // keep barcode
              targetBarcode = itemToMove.barcode;
              quantity = this.getInventoryQuantity(itemToMove);
            }

            this.moveInventoryItemQuantity(itemToMove.inventoryItemId, quantity,
              dialogResult.site, dialogResult.location, dialogResult.subLocation, targetBarcode);
          }
        });
    }

  public moveInventoryItemQuantity(inventoryItemIdToMove: number, quantity: number, siteId: number, locationId: number, subLocationId: number, targetBarcode: string) : void {
    this.spinnerText = "Updating Inventory Item...";
    this.showSpinner = true;

    var url = `inventory/MoveInventoryItemQuantity?inventoryItemIdToMove=${inventoryItemIdToMove}&quantity=${quantity}&siteId=${siteId}&locationId=${locationId}&subLocationId=${subLocationId}&targetBarcode=${targetBarcode}`;
    this.transferHttp.put(url, null)
      .subscribe({
        next: (retVal) => {
          this.showSpinner = false;
          this.loadInventoryItemByBarcode();
        },
        error: (e) => { this.showSpinner = false; }
      });
  }

  public getInventoryQuantity(inventoryItem: InvInventoryItem): number {
    const sum: number = inventoryItem.invInventoryItemReceived.filter(ir => ir.quantity > 0)
      .map(i => i.quantity)
      .reduce((sum, current) => sum + current, 0);
    return sum;
  }

  public printBarcode() {
    if (this.inventoryItem?.barcode != null) {
      let printBarcodeRoute: string = `../reporting/InventoryBarcode/trdp/${JSON.stringify({ Barcode: this.inventoryItem.barcode })}`;
      this.router.navigate([printBarcodeRoute]);
    }
  }

  public printBarcodeSize(barcodesize: string) {
    if (this.inventoryItem?.barcode != null) {
      let printBarcodeRoute: string = `../reporting/${barcodesize}/trdp/${JSON.stringify({ Barcode: this.inventoryItem.barcode })}`;
      this.router.navigate([printBarcodeRoute]);
    }
  }

  public printBarcode2x4 = () =>  this.printBarcodeSize('InventoryBarcode2x4');
  public printBarcode1x2 = () =>  this.printBarcodeSize('InventoryBarcode1x2');
  public printBarcode075x2 = () =>  this.printBarcodeSize('InventoryBarcode075x2');
  public printBarcode05x15 = () =>  this.printBarcodeSize('InventoryBarcode05x15');
  public printBarcode375x2 = () =>  this.printBarcodeSize('InventoryBarcode0375x2');

  //Prevents routing when unsaved changes
  canDeactivate(): Observable<boolean> {
    if (this.invForm.dirty) {
        return this.openUnsavedChangesConfirmModal()
    } else {
        return of(true)
    }
  }

  openUnsavedChangesConfirmModal():Observable<boolean>{
    var messsage = this.messageService.getDialogMessage(DialogComponent.UnsavedChanges)
    let dialogRef = this.dialog.open(AimsCommonConfirmDialogComponent, {
        data: messsage
    });
    return dialogRef.afterClosed();
  }

  public createReq() {
    if (isPlatformBrowser(this.platformId)) {
      window.open(`/logistics/reqFromInventory/${this.inventoryItem.inventoryItemId}`, '_blank');
    }
  }

}
